import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Phone, ExternalLink, Plane, TrainFront, Bus, CarTaxiFront, Users, Car, Hotel, Map as MapIcon, MessagesSquare, Stamp } from "lucide-react";
import { PageHeader, Spinner, Pill } from "../components/common";
import Breadcrumbs from "../components/Breadcrumbs";
import { MissionMap, Disclaimer, MISSION_TYPE_LABELS } from "./VisasHub";
import { SuggestCorrection, PublicNotes, AdminNotesPanel, LastVerified } from "../components/VisaWidgets";
import { SITE } from "../lib/site";
import api from "../lib/api";

export default function MissionProfile() {
  const { id } = useParams();
  const [m, setM] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setM(null); setErr(false);
    api.get(`/visas/missions/${id}`).then((r) => {
      setM(r.data);
      document.title = `${r.data.name} — Address, Contact & Services | ${SITE.brandDomain}`;
    }).catch(() => setErr(true));
  }, [id]);

  if (err) return (
    <div className="max-w-7xl mx-auto px-5 py-20 text-center" data-testid="mission-not-found">
      <p className="text-muted-foreground">Diplomatic mission not found.</p>
      <Link to="/visas" className="text-primary font-700 hover:underline mt-2 inline-block">← Passports & Visas</Link>
    </div>
  );
  if (!m) return <Spinner full />;

  const cityLink = `/cities/${encodeURIComponent(m.city)}?country=${m.host_iso2 || ""}`;
  const travelLinks = [
    ...(m.nearby_airports || []).map((a) => ({
      icon: Plane, label: `${a.name} (${a.iata_code})`, sub: `${a.distance_km} km away`, to: `/airports/${a.iata_code}`,
    })),
    { icon: MapIcon, label: `${m.city} city guide`, sub: "Local attractions, weather & transport", to: cityLink },
    { icon: TrainFront, label: "Rail travel", sub: "Stations & operators", to: "/rail" },
    { icon: Bus, label: "Bus & coach", to: "/bus" },
    { icon: CarTaxiFront, label: "Taxi & ground transport", to: "/ground" },
    { icon: Users, label: "Rideshare", to: "/rideshare" },
    { icon: Car, label: "Road & rental cars", to: "/road" },
    { icon: Hotel, label: "Hotels & places to stay", to: "/stays" },
    { icon: Stamp, label: `${m.host_country} passport & visa info`, to: m.host_country_page ? `/visas/${m.host_country_page.id}` : "/visas" },
    { icon: MessagesSquare, label: "Traveler community", to: "/community" },
  ];

  return (
    <div data-testid={`mission-profile-${m.id}`}>
      <PageHeader eyebrow={`${SITE.name} · ${MISSION_TYPE_LABELS[m.mission_type] || m.mission_type}`} title={m.name}
        entityCode={m.represented_iso2}
        subtitle={`${MISSION_TYPE_LABELS[m.mission_type] || m.mission_type} of ${m.represented_country} in ${m.city}, ${m.host_country}.`}>
        <Breadcrumbs className="mt-4 text-zinc-300" items={[
          { label: "Visas & Embassies", to: "/visas" },
          { label: m.host_country, to: m.host_country_page ? `/visas/${m.host_country_page.id}` : "/visas" },
          { label: m.city, to: cityLink },
          { label: m.name },
        ]} />
      </PageHeader>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10 space-y-10">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Contact & identity */}
          <div className="lg:col-span-2 space-y-6">
            <div className="border border-zinc-300 bg-white p-6" data-testid="mission-contact-card">
              <div className="flex flex-wrap gap-1.5 mb-4">
                <Pill tone="blue">{MISSION_TYPE_LABELS[m.mission_type] || m.mission_type}</Pill>
                <Pill tone="green">Represents {m.represented_country}</Pill>
                <Pill>Host: {m.host_country}</Pill>
              </div>
              <div className="space-y-2.5 text-sm">
                <div className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-[#0e7490] shrink-0" /><span>{m.address}, {m.city}, {m.host_country}</span></div>
                {m.phone && <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-[#0e7490] shrink-0" /><a href={`tel:${m.phone}`} className="hover:underline">{m.phone}</a></div>}
                {m.website && (
                  <div className="flex items-center gap-2"><ExternalLink className="w-4 h-4 text-[#0e7490] shrink-0" />
                    <a href={m.website} target="_blank" rel="noreferrer" data-testid="mission-website-link" className="text-primary font-600 hover:underline">Official website</a>
                  </div>
                )}
              </div>
              {m.jurisdiction && (
                <div className="mt-4 border-t border-zinc-100 pt-3">
                  <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">Consular jurisdiction</div>
                  <p className="text-sm text-zinc-700" data-testid="mission-jurisdiction">{m.jurisdiction}</p>
                </div>
              )}
              {m.services?.length > 0 && (
                <div className="mt-4 border-t border-zinc-100 pt-3">
                  <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1.5">Services</div>
                  <div className="flex flex-wrap gap-1.5">
                    {m.services.map((s) => <Pill key={s} tone="muted">{s.replaceAll("_", " ")}</Pill>)}
                  </div>
                </div>
              )}
              <div className="mt-4 border-t border-zinc-100 pt-3 flex items-center justify-between flex-wrap gap-2">
                <LastVerified date={m.last_verified} source={m.source_org} />
              </div>
            </div>

            <PublicNotes notes={m.public_notes} />

            {m.represented_country_page?.links && Object.keys(m.represented_country_page.links).length > 0 && (
              <div className="border border-zinc-300 bg-white p-6" data-testid="mission-country-links">
                <h2 className="font-heading font-700 text-lg md:text-lg mb-3">{m.represented_country} official visa &amp; passport resources</h2>
                <Link to={`/visas/${m.represented_country_page.id}`} className="text-sm text-primary font-700 hover:underline">
                  View the full {m.represented_country} passport &amp; visa guide →
                </Link>
              </div>
            )}

            <SuggestCorrection entityType="mission" entityId={m.id} entityName={m.name} />
            <AdminNotesPanel entityType="mission" entityId={m.id} />
          </div>

          {/* Map */}
          <div>
            {m.lat != null && m.lon != null ? (
              <MissionMap missions={[m]} height="320px" />
            ) : (
              <div className="border border-dashed border-zinc-300 p-8 text-center text-sm text-muted-foreground">Location coordinates not yet verified.</div>
            )}
          </div>
        </div>

        {/* Traveling here? (§39) */}
        <section data-testid="mission-traveling-here">
          <h2 className="font-heading font-700 text-lg md:text-lg mb-1">Traveling here?</h2>
          <p className="text-sm text-muted-foreground mb-4">Connect this mission to the rest of your journey on {SITE.brandLabel}.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {travelLinks.map((l, i) => (
              <Link key={i} to={l.to} data-testid={`travel-link-${i}`}
                className="flex items-center gap-3 border border-zinc-300 bg-white px-4 py-3 hover:border-[#0e7490] hover:-translate-y-px transition-[transform,border-color]">
                <l.icon className="w-5 h-5 text-[#0e7490] shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-600 truncate">{l.label}</div>
                  {l.sub && <div className="text-xs text-muted-foreground truncate">{l.sub}</div>}
                </div>
              </Link>
            ))}
          </div>
        </section>

        <Disclaimer text={m.disclaimer} />
      </div>
    </div>
  );
}
